import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChefHat, Info, Plus, RefreshCw, Search } from 'lucide-react';
import PosCaisseSharedHeader from '../components/caisse/PosCaisseSharedHeader';
import { canPosAction, getAdminUserFromStorage } from '../utils/posStaffAccess';
import { useRestaurantPrepConfig } from '../hooks/useRestaurantPrepConfig';
import { getProductImageUrl } from '../utils/productImage';
import { matchSearchText } from '../utils/normalizeSearchText';
import {
  ASSIGN_NO_PREP,
  ASSIGN_PREP_NO_STATION,
  PREP_KIND_OPTIONS,
  assignSelectValue,
  computePrepSummary,
  productPrepStatusLabel,
  sortProductsStable,
  suggestStationCodeFromLabel,
} from '../utils/restaurantPrepConfigModel';

function filterChipClass(active) {
  return active
    ? 'ring-2 ring-emerald-600 bg-emerald-50 border-emerald-300 text-emerald-950'
    : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50';
}

function resolveCommercantId(searchParams) {
  const fromUrl = searchParams.get('commercantId');
  if (fromUrl) return fromUrl;
  try {
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    return user.commercantId || user.commercant?.id || null;
  } catch {
    return null;
  }
}

export default function RestaurantPrepConfiguration() {
  const [searchParams] = useSearchParams();
  const commercantId = resolveCommercantId(searchParams);
  const listRef = useRef(null);
  const stationLabelRef = useRef(null);
  const scrollSnapshot = useRef(0);

  const {
    stations,
    products,
    initialLoading,
    loadError,
    stationBusy,
    productSaveState,
    reload,
    upsertStation,
    assignOneProduct,
    assignManyProducts,
  } = useRestaurantPrepConfig(commercantId);

  const [toast, setToast] = useState('');
  const [draft, setDraft] = useState({ code: '', label: '', kind: 'OTHER' });
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stationFilter, setStationFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkTarget, setBulkTarget] = useState('');
  const [stationEdits, setStationEdits] = useState({});

  const showToast = (msg, kind = 'ok') => {
    setToast(kind === 'error' ? `⚠ ${msg}` : msg);
    window.setTimeout(() => setToast(''), 5000);
  };

  const activeStations = useMemo(
    () => stations.filter((s) => s.actif !== false),
    [stations]
  );

  const stationById = useMemo(() => new Map(stations.map((s) => [s.id, s])), [stations]);

  const categories = useMemo(() => {
    const set = new Set();
    for (const p of products) {
      const c = p.categorie || p.rubriqueNom || p.catalogueRubrique?.nom;
      if (c) set.add(String(c));
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = sortProductsStable(products);
    const q = search.trim();
    if (q) {
      list = list.filter((p) => matchSearchText(p.nom, q) || matchSearchText(p.categorie, q));
    }
    if (categoryFilter) {
      list = list.filter(
        (p) => String(p.categorie || p.rubriqueNom || '') === categoryFilter
      );
    }
    if (stationFilter === 'no_prep') {
      list = list.filter((p) => !p.requiresPreparation);
    } else if (stationFilter === 'prep_no_station') {
      list = list.filter((p) => p.requiresPreparation && !p.preparationStationId);
    } else if (stationFilter === 'unassigned') {
      list = list.filter((p) => !p.preparationStationId);
    } else if (stationFilter === 'on_any_station') {
      list = list.filter((p) => Boolean(p.preparationStationId));
    } else if (stationFilter === 'inactive_station') {
      list = list.filter((p) => {
        if (!p.preparationStationId) return false;
        const st = stationById.get(p.preparationStationId);
        return st?.actif === false;
      });
    } else if (stationFilter !== 'all' && stationFilter) {
      list = list.filter((p) => p.preparationStationId === stationFilter);
    }
    return list;
  }, [products, search, categoryFilter, stationFilter, stationById]);

  const summary = useMemo(() => computePrepSummary(products, stations), [products, stations]);

  const filteredIds = useMemo(() => filteredProducts.map((p) => p.id), [filteredProducts]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const captureScroll = () => {
    if (listRef.current) scrollSnapshot.current = listRef.current.scrollTop;
  };

  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = scrollSnapshot.current;
  }, [products, productSaveState, filteredProducts.length]);

  const toggleSelect = (id) => {
    captureScroll();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleStationFilter = (value) => {
    captureScroll();
    setStationFilter((prev) => (prev === value ? 'all' : value));
  };

  const clearAllFilters = () => {
    captureScroll();
    setSearch('');
    setCategoryFilter('');
    setStationFilter('all');
  };

  const hasActiveFilters =
    Boolean(search.trim()) || Boolean(categoryFilter) || stationFilter !== 'all';

  const clearSelection = () => {
    captureScroll();
    setSelectedIds(new Set());
  };

  const toggleSelectAllFiltered = () => {
    captureScroll();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const id of filteredIds) next.delete(id);
      } else {
        for (const id of filteredIds) next.add(id);
      }
      return next;
    });
  };

  const createStation = async (preset) => {
    const label = (preset?.label || draft.label).trim();
    const kind = preset?.kind || draft.kind;
    let code = (preset?.code || draft.code).trim();
    if (!label) {
      showToast('Le nom du poste est requis', 'error');
      return;
    }
    if (!code) {
      code = suggestStationCodeFromLabel(
        label,
        stations.map((s) => s.code)
      );
    }
    try {
      await upsertStation({ code: code.toUpperCase(), label, kind, actif: true });
      showToast(`Poste « ${label} » enregistré`);
      setDraft({ code: '', label: '', kind: 'OTHER' });
    } catch (e) {
      showToast(e.message || 'Création impossible', 'error');
    }
  };

  const saveStationEdit = async (station) => {
    const edit = stationEdits[station.id] || {};
    const label = (edit.label ?? station.label).trim();
    const kind = edit.kind ?? station.kind;
    if (!label) {
      showToast('Le nom du poste est requis', 'error');
      return;
    }
    try {
      await upsertStation({
        code: station.code,
        label,
        kind,
        actif: station.actif !== false,
        sortOrder: station.sortOrder,
      });
      showToast('Poste mis à jour');
      setStationEdits((prev) => {
        const next = { ...prev };
        delete next[station.id];
        return next;
      });
    } catch (e) {
      showToast(e.message || 'Mise à jour impossible', 'error');
    }
  };

  const toggleStationActive = async (station) => {
    const assigned = products.filter((p) => p.preparationStationId === station.id).length;
    if (station.actif !== false && assigned > 0) {
      const ok = window.confirm(
        `${assigned} produit(s) sont encore affectés à « ${station.label} ». ` +
          'Ils conserveront ce poste mais les nouveaux envois KDS exigeront un poste actif. Désactiver ?'
      );
      if (!ok) return;
    }
    try {
      await upsertStation({
        code: station.code,
        label: station.label,
        kind: station.kind,
        actif: station.actif === false,
      });
      showToast(station.actif === false ? 'Poste réactivé' : 'Poste désactivé');
    } catch (e) {
      showToast(e.message || 'Mise à jour impossible', 'error');
    }
  };

  const onAssignOne = async (produitId, raw) => {
    captureScroll();
    const res = await assignOneProduct(produitId, raw);
    if (res.ok) showToast('Produit enregistré');
    else showToast(res.error?.message || 'Enregistrement impossible', 'error');
  };

  const applyBulkWith = async (rawTarget) => {
    const target = rawTarget ?? bulkTarget;
    if (!target) {
      showToast('Choisissez une destination', 'error');
      return;
    }
    const ids = [...selectedIds];
    if (!ids.length) {
      showToast('Sélectionnez au moins un produit', 'error');
      return;
    }
    captureScroll();
    const res = await assignManyProducts(ids, target);
    if (res.ok) {
      showToast(`${ids.length} produit(s) mis à jour`);
      setSelectedIds(new Set());
      setBulkTarget('');
    } else if (res.outcome?.failed) {
      const failedSet = new Set(
        (res.outcome.results || []).filter((r) => !r.ok).map((r) => r.produitId)
      );
      setSelectedIds(failedSet);
      showToast(
        `${res.outcome.succeeded} réussi(s), ${res.outcome.failed} échec(s) — sélection ajustée`,
        'error'
      );
    } else {
      showToast(res.error?.message || 'Affectation groupée impossible', 'error');
    }
  };

  const applyBulk = () => applyBulkWith(bulkTarget);

  const saveStatusLine = useMemo(() => {
    if (!productSaveState) return null;
    if (productSaveState.status === 'saving') return 'Enregistrement en cours…';
    if (productSaveState.status === 'saved') return 'Modifications enregistrées';
    if (productSaveState.status === 'partial') {
      return `Enregistrement partiel (${productSaveState.succeeded} OK, ${productSaveState.failed} échec)`;
    }
    if (productSaveState.status === 'error') {
      return productSaveState.message || 'Erreur enregistrement';
    }
    return null;
  }, [productSaveState]);

  const restaurantBack = commercantId
    ? `/caisse/restaurant?commercantId=${encodeURIComponent(commercantId)}&config=prep`
    : '/caisse/restaurant';

  const canAdmin = canPosAction(getAdminUserFromStorage(), 'caisse:restaurant:admin');

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="restaurant-prep-config-page">
      <PosCaisseSharedHeader commercantId={commercantId} title="Préparation & postes" />
      <header className="px-4 pt-4 flex flex-wrap items-center gap-2">
        <Link
          to={restaurantBack}
          className="min-h-[44px] inline-flex items-center gap-2 px-3 rounded-xl border bg-white text-sm"
          data-testid="restaurant-prep-config-back"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Restaurant — Configuration
        </Link>
        <Link
          to={`/caisse/kitchen?commercantId=${encodeURIComponent(commercantId || '')}`}
          className="min-h-[44px] inline-flex items-center gap-2 px-3 rounded-xl border bg-white text-sm text-emerald-800"
          data-testid="restaurant-prep-config-open-kds"
        >
          <ChefHat className="w-4 h-4" aria-hidden />
          Écran cuisine (KDS)
        </Link>
        <button
          type="button"
          className="ml-auto min-h-[44px] px-3 rounded-xl border bg-white"
          onClick={() => reload({ soft: true })}
          aria-label="Actualiser"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </header>

      {toast && (
        <div className="mx-4 mt-3 rounded-xl bg-slate-900 text-white px-4 py-3 text-sm" role="status">
          {toast}
        </div>
      )}

      <main className="p-4 max-w-6xl mx-auto space-y-6" data-testid="restaurant-prep-config">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Préparation & postes</h1>
          <p className="text-sm text-slate-600 mt-1">
            Organisez vos postes de préparation et affectez le catalogue. Les intitulés sont libres :
            Cuisine, Bar, Sushi, Grillades, etc.
          </p>
        </div>

        {!canAdmin && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
            Droits insuffisants pour configurer les postes de préparation.
          </p>
        )}

        {initialLoading && (
          <p className="text-sm text-slate-500" data-testid="restaurant-prep-config-loading">
            Chargement…
          </p>
        )}
        {loadError && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">{loadError}</p>
        )}

        {canAdmin && !initialLoading && !loadError && (
          <>
            <section className="bg-white rounded-2xl border p-4 space-y-4" aria-labelledby="prep-stations-heading">
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="prep-stations-heading" className="font-semibold text-lg">
                  Mes postes de préparation
                </h2>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 space-y-2">
                <p className="text-sm font-medium text-slate-800">Création rapide</p>
                <p className="text-xs text-slate-600">
                  Exemples à personnaliser — vous pouvez renommer librement avant enregistrement.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="min-h-[44px] px-3 rounded-xl border border-orange-200 bg-white text-orange-950 text-sm"
                    disabled={stationBusy}
                    data-testid="restaurant-prep-preset-cuisine"
                    onClick={() => {
                      setDraft({ code: '', label: 'Cuisine', kind: 'KITCHEN' });
                    }}
                  >
                    Cuisine
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] px-3 rounded-xl border border-sky-200 bg-white text-sky-950 text-sm"
                    disabled={stationBusy}
                    data-testid="restaurant-prep-preset-bar"
                    onClick={() => {
                      setDraft({ code: '', label: 'Bar', kind: 'BAR' });
                    }}
                  >
                    Bar
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] px-4 rounded-xl bg-slate-900 text-white text-sm font-medium inline-flex items-center gap-2"
                    disabled={stationBusy}
                    data-testid="restaurant-prep-quick-add-station"
                    onClick={() => {
                      setDraft({ code: '', label: '', kind: 'OTHER' });
                      window.setTimeout(() => {
                        stationLabelRef.current?.focus();
                        stationLabelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 0);
                    }}
                  >
                    <Plus className="w-4 h-4" aria-hidden />
                    Ajouter un poste
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-[1fr_minmax(8rem,0.45fr)] gap-2 items-end">
                <label className="block text-xs text-slate-600">
                  Nom affiché
                  <input
                    ref={stationLabelRef}
                    className="mt-1 w-full border rounded-xl px-3 min-h-[44px] text-sm"
                    placeholder="Ex. Sushi froid, Grillades, Pâtisserie…"
                    value={draft.label}
                    onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                    data-testid="restaurant-prep-station-label"
                  />
                </label>
                <label className="block text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1">
                    Catégorie KDS
                    <Info className="w-3.5 h-3.5 text-slate-400" aria-hidden title="Oriente l’écran cuisine ; le nom du poste reste libre." />
                  </span>
                  <select
                    className="mt-1 w-full border rounded-xl px-3 min-h-[44px] text-sm"
                    value={draft.kind}
                    onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value }))}
                    title={PREP_KIND_OPTIONS.find((o) => o.value === draft.kind)?.hint}
                  >
                    {PREP_KIND_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} title={o.hint}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <details className="text-sm text-slate-600">
                <summary className="cursor-pointer min-h-[44px] flex items-center">
                  Paramètres avancés (code technique)
                </summary>
                <label className="block mt-2 text-xs">
                  Code unique (optionnel)
                  <input
                    className="mt-1 w-full max-w-md border rounded-xl px-3 min-h-[44px] text-sm font-mono"
                    placeholder="Généré automatiquement si vide"
                    value={draft.code}
                    onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
                    data-testid="restaurant-prep-station-code"
                  />
                </label>
                <p className="text-xs mt-1 text-slate-500">
                  Les codes existants ne sont jamais modifiés — réservé à la création d’un nouveau poste.
                </p>
              </details>
              <button
                type="button"
                className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl bg-emerald-600 text-white text-sm font-medium"
                disabled={stationBusy}
                data-testid="restaurant-prep-station-save"
                onClick={() => createStation()}
              >
                Enregistrer le poste
              </button>

              {stations.length > 0 && (
                <div
                  className="hidden lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.7fr)_minmax(0,0.55fr)_minmax(0,0.75fr)_repeat(3,minmax(0,auto))] gap-2 px-3 text-xs font-medium text-slate-500"
                  aria-hidden
                >
                  <span>Nom</span>
                  <span>Catégorie</span>
                  <span>Code</span>
                  <span>Statut</span>
                  <span>Produits</span>
                  <span className="col-span-3 text-right">Actions</span>
                </div>
              )}
              <ul className="space-y-2" data-testid="restaurant-prep-stations-list">
                {stations.length === 0 && (
                  <li className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    Aucun poste — créez le premier avec un nom libre.
                  </li>
                )}
                {stations.map((s) => {
                  const edit = stationEdits[s.id] || {};
                  const count = summary.perStation[s.id] || 0;
                  return (
                    <li
                      key={s.id}
                      className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.7fr)_minmax(0,0.55fr)_minmax(0,0.75fr)_repeat(3,minmax(0,auto))] gap-2 items-center border rounded-xl px-3 py-2 text-sm"
                    >
                      <input
                        className="border rounded-lg min-h-[44px] px-2 w-full"
                        aria-label="Nom du poste"
                        value={edit.label ?? s.label}
                        onChange={(e) =>
                          setStationEdits((prev) => ({
                            ...prev,
                            [s.id]: { ...prev[s.id], label: e.target.value },
                          }))
                        }
                      />
                      <select
                        className="border rounded-lg min-h-[44px] px-2 text-xs w-full"
                        aria-label="Catégorie"
                        value={edit.kind ?? s.kind}
                        title={PREP_KIND_OPTIONS.find((o) => o.value === (edit.kind ?? s.kind))?.hint}
                        onChange={(e) =>
                          setStationEdits((prev) => ({
                            ...prev,
                            [s.id]: { ...prev[s.id], kind: e.target.value },
                          }))
                        }
                      >
                        {PREP_KIND_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value} title={o.hint}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-slate-500 font-mono truncate" title={s.code}>
                        {s.code}
                      </span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full text-center ${s.actif === false ? 'bg-slate-200' : 'bg-emerald-50 text-emerald-900'}`}
                      >
                        {s.actif === false ? 'Inactif' : 'Actif'}
                      </span>
                      <button
                        type="button"
                        className={`min-h-[44px] px-2 rounded-lg border text-xs font-medium w-full ${filterChipClass(stationFilter === s.id)}`}
                        data-testid={`restaurant-prep-station-filter-${s.id}`}
                        onClick={() => toggleStationFilter(s.id)}
                        title="Filtrer les produits de ce poste"
                      >
                        {count} prod.
                      </button>
                      <button
                        type="button"
                        className="min-h-[44px] px-3 rounded-lg border text-xs whitespace-nowrap"
                        disabled={stationBusy}
                        onClick={() => saveStationEdit(s)}
                      >
                        Enregistrer
                      </button>
                      <button
                        type="button"
                        className="min-h-[44px] px-3 rounded-lg border text-xs whitespace-nowrap lg:col-span-2"
                        disabled={stationBusy}
                        onClick={() => toggleStationActive(s)}
                      >
                        {s.actif === false ? 'Réactiver' : 'Désactiver'}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {stations.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="restaurant-prep-station-chips">
                  <button
                    type="button"
                    className={`min-h-[44px] px-3 rounded-xl border text-sm ${filterChipClass(stationFilter === 'all')}`}
                    data-testid="restaurant-prep-filter-all"
                    onClick={() => toggleStationFilter('all')}
                  >
                    Tous les produits
                  </button>
                  {stations.map((s) => (
                    <button
                      key={`chip-${s.id}`}
                      type="button"
                      className={`min-h-[44px] px-3 rounded-xl border text-sm ${filterChipClass(stationFilter === s.id)}`}
                      data-testid={`restaurant-prep-station-chip-${s.id}`}
                      onClick={() => toggleStationFilter(s.id)}
                    >
                      {s.label}
                      <span className="ml-1 text-slate-500">({summary.perStation[s.id] || 0})</span>
                      {s.actif === false ? ' · inactif' : ''}
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white rounded-2xl border p-4 space-y-3" aria-labelledby="prep-summary-heading">
              <h2 id="prep-summary-heading" className="font-semibold text-lg">
                Résumé et vérifications
              </h2>
              <div className="flex flex-wrap gap-2 text-sm" data-testid="restaurant-prep-product-summary">
                <button
                  type="button"
                  className={`min-h-[44px] px-3 rounded-lg border ${filterChipClass(stationFilter === 'all' && !categoryFilter && !search.trim())}`}
                  onClick={clearAllFilters}
                >
                  {summary.total} produits catalogue
                </button>
                <button
                  type="button"
                  className={`min-h-[44px] px-3 rounded-lg border ${filterChipClass(stationFilter === 'on_any_station')}`}
                  data-testid="restaurant-prep-summary-on-station"
                  onClick={() => toggleStationFilter('on_any_station')}
                >
                  {summary.onStation} affectés à un poste
                </button>
                <button
                  type="button"
                  className={`min-h-[44px] px-3 rounded-lg border ${filterChipClass(stationFilter === 'no_prep')}`}
                  data-testid="restaurant-prep-summary-no-prep"
                  onClick={() => toggleStationFilter('no_prep')}
                >
                  {summary.noPrep} sans préparation
                </button>
                <button
                  type="button"
                  className={`min-h-[44px] px-3 rounded-lg border ${filterChipClass(stationFilter === 'prep_no_station')}`}
                  data-testid="restaurant-prep-summary-prep-no-station"
                  onClick={() => toggleStationFilter('prep_no_station')}
                >
                  {summary.prepNoStation} préparation sans poste
                </button>
                {summary.inactiveStationProducts > 0 && (
                  <button
                    type="button"
                    className={`min-h-[44px] px-3 rounded-lg border ${filterChipClass(stationFilter === 'inactive_station')}`}
                    data-testid="restaurant-prep-summary-inactive-station"
                    onClick={() => toggleStationFilter('inactive_station')}
                  >
                    {summary.inactiveStationProducts} sur poste inactif
                  </button>
                )}
              </div>
              {summary.inactiveReferenced.length > 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  Postes inactifs encore référencés :{' '}
                  {summary.inactiveReferenced.map((st) => st.label).join(', ')}
                </p>
              )}
            </section>

            <section className="bg-white rounded-2xl border p-4 space-y-3" aria-labelledby="prep-products-heading">
              <h2 id="prep-products-heading" className="font-semibold text-lg">
                Affectation des produits
              </h2>
              {saveStatusLine && (
                <p className="text-xs text-slate-600" role="status" data-testid="restaurant-prep-save-status">
                  {saveStatusLine}
                </p>
              )}
              {hasActiveFilters && (
                <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
                  Filtre actif
                  {stationFilter !== 'all' && (
                    <span className="font-medium" data-testid="restaurant-prep-active-filter-label">
                      {stationFilter === 'no_prep' && 'Sans préparation'}
                      {stationFilter === 'prep_no_station' && 'Préparation sans poste'}
                      {stationFilter === 'unassigned' && 'Sans poste affecté'}
                      {stationFilter === 'on_any_station' && 'Affectés à un poste'}
                      {stationFilter === 'inactive_station' && 'Poste inactif'}
                      {stationById.has(stationFilter) && stationById.get(stationFilter)?.label}
                    </span>
                  )}
                  {categoryFilter && <span>· cat. {categoryFilter}</span>}
                  {search.trim() && <span>· recherche « {search.trim()} »</span>}
                  <button
                    type="button"
                    className="underline min-h-[36px]"
                    data-testid="restaurant-prep-clear-filters"
                    onClick={clearAllFilters}
                  >
                    Effacer les filtres
                  </button>
                </p>
              )}
              <div className="flex flex-wrap gap-2 items-end">
                <label className="flex-1 min-w-[200px]">
                  <span className="text-xs text-slate-600 block mb-1">Recherche</span>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      className="w-full border rounded-xl pl-9 pr-3 min-h-[44px] text-sm"
                      placeholder="Nom du produit…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="restaurant-prep-product-filter"
                    />
                  </div>
                </label>
                <label className="min-w-[160px]">
                  <span className="text-xs text-slate-600 block mb-1">Catégorie</span>
                  <select
                    className="w-full border rounded-xl px-3 min-h-[44px] text-sm"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    data-testid="restaurant-prep-filter-category"
                  >
                    <option value="">Toutes</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="min-w-[180px]">
                  <span className="text-xs text-slate-600 block mb-1">Filtre poste</span>
                  <select
                    className="w-full border rounded-xl px-3 min-h-[44px] text-sm"
                    value={stationFilter}
                    onChange={(e) => setStationFilter(e.target.value)}
                    data-testid="restaurant-prep-filter-station"
                  >
                    <option value="all">Tous</option>
                    <option value="no_prep">Sans préparation</option>
                    <option value="prep_no_station">Préparation sans poste</option>
                    <option value="unassigned">Sans poste affecté</option>
                    {stations.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                        {s.actif === false ? ' (inactif)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex flex-wrap gap-2 items-center border rounded-xl p-3 bg-slate-50">
                <label className="inline-flex items-center gap-2 min-h-[44px] text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 shrink-0"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAllFiltered}
                    data-testid="restaurant-prep-select-all-filtered"
                  />
                  Tout sélectionner ({filteredIds.length} affichés)
                </label>
                {!hasActiveFilters && (
                  <button
                    type="button"
                    className="text-sm text-slate-600 underline min-h-[44px]"
                    onClick={clearAllFilters}
                  >
                    Tous les produits
                  </button>
                )}
              </div>

              {selectedIds.size > 0 && (
                <div
                  className="sticky bottom-2 z-20 flex flex-wrap gap-2 items-center border-2 border-emerald-500 rounded-xl p-3 bg-white shadow-lg"
                  data-testid="restaurant-prep-bulk-bar"
                  role="region"
                  aria-label="Affectation groupée"
                >
                  <span className="font-semibold text-emerald-900 min-h-[44px] inline-flex items-center">
                    {selectedIds.size} sélectionné(s)
                  </span>
                  <select
                    className="border rounded-lg min-h-[44px] px-2 text-sm flex-1 min-w-[180px]"
                    value={bulkTarget}
                    onChange={(e) => setBulkTarget(e.target.value)}
                    data-testid="restaurant-prep-bulk-target"
                    aria-label="Destination affectation groupée"
                  >
                    <option value="">Choisir un poste…</option>
                    {activeStations.map((s) => (
                      <option key={s.id} value={s.id}>
                        Poste : {s.label}
                      </option>
                    ))}
                    <option value={ASSIGN_PREP_NO_STATION}>Préparation — poste à choisir</option>
                  </select>
                  <button
                    type="button"
                    className="min-h-[44px] px-4 rounded-xl bg-emerald-600 text-white text-sm font-medium"
                    onClick={applyBulk}
                    data-testid="restaurant-prep-bulk-apply"
                  >
                    Affecter
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] px-3 rounded-xl border text-sm"
                    onClick={() => applyBulkWith(ASSIGN_NO_PREP)}
                    data-testid="restaurant-prep-bulk-no-prep"
                  >
                    Sans préparation
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] px-3 rounded-xl border text-sm"
                    onClick={clearSelection}
                    data-testid="restaurant-prep-bulk-deselect"
                  >
                    Désélectionner
                  </button>
                </div>
              )}

              <ul
                ref={listRef}
                className="space-y-2 border rounded-xl p-2 pb-4"
                data-testid="restaurant-prep-products-list"
              >
                {filteredProducts.map((p) => {
                  const img = getProductImageUrl(p);
                  const status = productPrepStatusLabel(p, stationById);
                  const saving =
                    productSaveState?.kind === 'single' &&
                    productSaveState?.produitId === p.id &&
                    productSaveState?.status === 'saving';
                  const selected = selectedIds.has(p.id);
                  return (
                    <li
                      key={p.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      data-testid={`restaurant-prep-product-row-${p.id}`}
                      className={`flex flex-wrap items-center gap-2 border rounded-xl px-3 py-2 text-sm cursor-pointer transition-colors min-h-[56px] ${
                        selected
                          ? 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-200'
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() => toggleSelect(p.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleSelect(p.id);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        className="w-5 h-5 shrink-0 cursor-pointer"
                        checked={selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelect(p.id)}
                        aria-label={`Sélectionner ${p.nom}`}
                      />
                      {img ? (
                        <img src={img} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0 pointer-events-none" />
                      ) : (
                        <div className="w-11 h-11 rounded-lg bg-slate-100 shrink-0 pointer-events-none" aria-hidden />
                      )}
                      <div className="flex-1 min-w-[120px] pointer-events-none">
                        <div className="font-medium">{p.nom}</div>
                        <div className="text-xs text-slate-500">
                          {p.categorie || '—'} · {status}
                        </div>
                      </div>
                      <select
                        className="border rounded-lg min-h-[44px] px-2 text-xs max-w-[240px] bg-white"
                        value={assignSelectValue(p)}
                        disabled={saving}
                        data-testid={`restaurant-prep-product-station-${p.id}`}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => onAssignOne(p.id, e.target.value)}
                      >
                        <option value={ASSIGN_NO_PREP}>Sans préparation</option>
                        <option value={ASSIGN_PREP_NO_STATION}>Préparation — poste à choisir</option>
                        {activeStations.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      {saving && <span className="text-xs text-slate-500">…</span>}
                    </li>
                  );
                })}
                {filteredProducts.length === 0 && (
                  <li className="text-sm text-slate-500 p-4 text-center">Aucun produit pour ce filtre.</li>
                )}
              </ul>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
